import { useContext } from "react";
import { SearchHeader } from "../../components/brand/SearchHeader";
import { SortType, SortDirection } from "../../components/common/SortFilter";
import { ProgramContext } from "../../contexts/program/context";
import { useProgramSearch } from "../../hooks/useProgramSearch";
import { ProgramDetails } from "../../services/program.service";
import { BrandCard } from "../../components/brand/BrandCard";
import { Pagination } from "../../components/common/Pagination";
import detective from "../../assets/detective.png";
import { EmptyState } from "@/components/ui/empty-state";

// Loading Skeleton Component
const BrandCardSkeleton = () => (
  <div className="bg-card rounded-xl p-6 shadow-sm animate-pulse">
    <div className="aspect-square flex items-center justify-center">
      <div className="w-[120px] h-[120px] bg-muted rounded-lg"></div>
    </div>
    <div className="h-4 bg-muted rounded mt-4"></div>
  </div>
);

export const SearchBrand = () => {
  const programContext = useContext(ProgramContext);
  const {
    searchTerm,
    setSearchTerm,
    programs,
    isLoading,
    total,
    totalPages,
    currentPage,
    setCurrentPage,
    setFilterBy,
    setSortDirection,
    applyFilters,
  } = useProgramSearch(20);

  const joinedProgramIds = new Set(
    programContext?.activePrograms?.map(
      (program: ProgramDetails) => program.id
    ) || []
  );

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSortChange = (type: SortType, direction: SortDirection) => {
    setFilterBy(type);
    setSortDirection(direction);
    applyFilters();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <main>
        <SearchHeader
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onSortChange={handleSortChange}
        />

        {/* Search Results */}
        {isLoading ? (
          <div className="mt-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <BrandCardSkeleton />
              <BrandCardSkeleton />
              <BrandCardSkeleton />
              <BrandCardSkeleton />
              <BrandCardSkeleton />
            </div>
          </div>
        ) : (
          <div className="mt-6">
            {/* Results count */}
            {searchTerm && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground">
                  {total} brands found
                </p>
              </div>
            )}

            <div className=" rounded-xl shadow-sm border border-border">
              <div className="p-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {programs.length > 0 ? (
                    programs.map((program) => (
                      <BrandCard
                        key={program.id}
                        program={program}
                        isJoined={joinedProgramIds.has(program.id)}
                      />
                    ))
                  ) : (
                    <div className="col-span-full">
                      <img
                        src={detective}
                        alt=""
                        className="w-48 mx-auto opacity-70"
                      />
                      <EmptyState
                        bordered={false}
                        className="py-4"
                        title="No brands found"
                        body={
                          searchTerm
                            ? "Try a broader search term"
                            : undefined
                        }
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && !isLoading && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </div>
        )}
    </main>
  );
};
